import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Switch,
  Radio,
  Loading,
  Modal,
  Text,
  Input,
  Button as NextButton
} from "@nextui-org/react";
import { useSelector } from "react-redux";
import { PaperPlus } from "react-iconly";
import { secureFetch } from "../../lib/api";
import { error } from "../../lib/userAlerts";
import { mstore } from "../../store";
import endpoint from "../../config/endpoint.json";
import CrossCompanySettings from './CrossCompanySettings';

const ManageArtifacts = () => {
  const [state, setState] = useState({
    artifactsList: [],
    addArtifacts: false,
    addArtifactsName: "",
    fetching: true,
    editButtonClicked: false,
    editIndex: null,
    renderKey: 0
  });

  const Access = useSelector(state => state.navigation.Access);
  const clientId = useSelector(state => 
    state.client?.id || mstore.getPath(["user", "company_id"])
  );

  const getArtifactsList = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, fetching: true }));
      const [ok, status, json] = await secureFetch(endpoint.getArtifactsList, {});
      if (ok) {
        setState(prev => ({
          ...prev,
          artifactsList: json.artifactsList,
          fetching: false,
        }));
      } else {
        error("Oops! error occurred during fetching artifacts list!");
      }
    } catch (err) {
      error("Oops! error occurred during fetching artifacts list!");
      console.error(err);
    }
  }, []);

  useEffect(() => {
    getArtifactsList();
  }, [clientId, getArtifactsList]);

  const handleActiveInactive = useCallback(async (obj) => {
    if (obj.status === 1) {
      await deleteArtifacts(obj);
    } else {
      await recoverArtifacts(obj);
    }
  }, []);

  const handleAutoCrop = useCallback(async (obj) => {
    if (obj.is_scanner_enable === 1) {
      await disableAutoCrop(obj);
    } else {
      await enableAutoCrop(obj);
    }
  }, []);

  const handlePartnerVisibility = useCallback(async (obj) => {
    if (obj.partner_visibility === 1) {
      await disablePartnerVisibility(obj);
    } else {
      await enablePartnerVisibility(obj);
    }
  }, []);

  const enablePartnerVisibility = useCallback(async (obj) => {
    try {
      const [ok, status, json] = await secureFetch(endpoint.enablePartnerVisibility, {
        type_name: obj.type_name
      });
      
      if (ok) {
        await getArtifactsList();
      } else {
        error("Oops! error occurred during enable partner visibility!");
      }
    } catch (err) {
      error("Oops! error occurred during enable partner visibility!");
      console.error(err);
    }
  }, [getArtifactsList]);
  
  const disablePartnerVisibility = useCallback(async (obj) => {
    try {
      const [ok, status, json] = await secureFetch(endpoint.disablePartnerVisibility, {
        type_name: obj.type_name
      });
      
      if (ok) {
        await getArtifactsList();
      } else {
        error("Oops! error occurred during disable partner visibility!");
      }
    } catch (err) {
      error("Oops! error occurred during disable partner visibility!");
      console.error(err);
    }
  }, [getArtifactsList]);

  const handleGrayScaleImage = useCallback(async (obj) => {
    try {
      const [ok, status, json] = await secureFetch(endpoint.updateIsGrayScaleEnable, {
        type_name: obj.type_name,
        grayScaleEnable: obj.is_grayscale_enable === 1 ? 0 : 1,
      });
      
      if (ok) {
        await getArtifactsList();
      } else {
        error("Oops! error occurred during update gray scale features!");
      }
    } catch (err) {
      error("Oops! error occurred during update gray scale features!");
      console.error(err);
    }
  }, [getArtifactsList]);

  const addArtifacts = useCallback(async () => {
    try {
      const [ok, status, json] = await secureFetch(endpoint.addArtifacts, {
        type_name: state.addArtifactsName,
      });
      
      if (ok) {
        await getArtifactsList();
        setState(prev => ({ 
          ...prev, 
          addArtifactsName: "",
          addArtifacts: false 
        }));
      } else {
        error("Oops! error occurred during add new artifacts!");
      }
    } catch (err) {
      error("Oops! error occurred during add new artifacts!");
      console.error(err);
    }
  }, [state.addArtifactsName, getArtifactsList]);

  const deleteArtifacts = useCallback(async (obj) => {
    try {
      const [ok, status, json] = await secureFetch(endpoint.deleteArtifacts, {
        type_name: obj.type_name
      });
      
      if (ok) {
        await getArtifactsList();
      } else {
        error("Oops! error occurred during delete artifacts!");
      }
    } catch (err) {
      error("Oops! error occurred during delete artifacts!");
      console.error(err);
    }
  }, [getArtifactsList]);
  
  const recoverArtifacts = useCallback(async (obj) => {
    try {
      const [ok, status, json] = await secureFetch(endpoint.recoverArtifacts, {
        type_name: obj.type_name
      });
      
      if (ok) {
        await getArtifactsList();
      } else {
        error("Oops! error occurred during recover artifacts!");
      }
    } catch (err) {
      error("Oops! error occurred during recover artifacts!");
      console.error(err);
    }
  }, [getArtifactsList]);
  
  const setIsPod = useCallback(async (obj) => {
    setState(prev => ({ ...prev, renderKey: prev.renderKey + 1 }));

    if (obj.is_osd === 1) {
      error("This artifacts type is already selected as OS&D");
      return;
    }

    if (obj.is_bol === 1) {
      error("This artifacts type is already selected as BOL");
      return;
    }

    try {
      const [ok, status, json] = await secureFetch(endpoint.setIsPod, { 
        type_name: obj.type_name 
      });
      
      if (ok) {
        await getArtifactsList();
      } else {
        error("Oops! error occurred during set is pod features!");
      }
    } catch (err) {
      error("Oops! error occurred during set is pod features!");
      console.error(err);
    }
  }, [getArtifactsList]);

  const removePod = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, renderKey: prev.renderKey + 1 }));

      const [ok, status, json] = await secureFetch(endpoint.removeIsPod, {});
      
      if (ok) {
        await getArtifactsList();
      } 
      else {
        error("Oops! error occurred during remove is pod features!");
      }
    } catch (err) {
      error("Oops! error occurred during remove is pod features!");
      console.error(err);
    }
  }, [getArtifactsList]);

  const setIsBOL = useCallback(async (obj) => {
    setState(prev => ({ ...prev, renderKey: prev.renderKey + 1 }));

    if (obj.is_pod === 1) {
      error("This artifacts type is already selected as POD");
      return;
    }

    if (obj.is_osd === 1) {
      error("This artifacts type is already selected as OS&D");
      return;
    }

    try {
      const [ok, status, json] = await secureFetch(endpoint.setIsBol, { 
        type_name: obj.type_name 
      });
      
      if (ok) {
        await getArtifactsList();
      } 
      else {
        error("Oops! error occurred during set BOL features!");
      }
    } catch (err) {
      error("Oops! error occurred during set BOL features!");
      console.error(err);
    }
  }, [getArtifactsList]);

  const removeBOL = useCallback(async () => {
    setState(prev => ({ ...prev, renderKey: prev.renderKey + 1 }));

    try {
      const [ok, status, json] = await secureFetch(endpoint.removeIsBol, {});
      if (ok) {
        await getArtifactsList();
      } 
      else {
        error("Oops! error occurred during remove is BOL features!");
      }
    } catch (err) {
      error("Oops! error occurred during remove is BOL features!");
      console.error(err);
    }
  }, [getArtifactsList]);

  const setIsOsd = useCallback(async (obj) => {
    setState(prev => ({ ...prev, renderKey: prev.renderKey + 1 })); 

    if (Access.allow_osd === 0) {
      error("Please enable os&d features");
      return;
    }

    if (obj.is_pod === 1) {
      error("This artifacts type is already selected as POD");
      return;
    }

    if (obj.is_bol === 1) {
      error("This artifacts type is already selected as BOL");
      return;
    }

    try {
      const [ok, status, json] = await secureFetch(endpoint.setIsOsd, { 
        type_name: obj.type_name 
      });
      
      if (ok) {
        await getArtifactsList();
      } 
      else {
        error("Oops! error occurred during set is OS&D features!");
      }
    } catch (err) {
      error("Oops! error occurred during set is OS&D features!");
      console.error(err);
    }
  }, [Access.allow_osd, getArtifactsList]);

  const removeOsd = useCallback(async () => {
    setState(prev => ({ ...prev, renderKey: prev.renderKey + 1 }));

    if (Access.allow_osd === 0) {
      error("Please enable os&d features");
      return;
    }

    try {
      const [ok, status, json] = await secureFetch(endpoint.removeIsOsd, {});
      if (ok) {
        await getArtifactsList();
      } 
      else {
        error("Oops! error occurred during remove is OS&D features!");
      }
    } catch (err) {
      error("Oops! error occurred during remove is OS&D features!");
      console.error(err);
    }
  }, [Access.allow_osd, getArtifactsList]);

  const renderUserRow = useCallback((obj, idx) => (
    <tr key={idx} className="flex">
      <td className="f5 fw2 flex flex-start items-center" style={{ width: '12.5%' }}>
        <div>{obj.type_name}</div>
      </td>
      <td className="f5 flex justify-center items-center h3" style={{ width: '12.5%' }}>
        <Switch
          size='sm'
          checked={obj.status === 1}
          onChange={() => handleActiveInactive(obj)}
        />
      </td>
      <td className="flex justify-center items-center h3" style={{ width: '12.5%' }}>
        <Switch
          size='sm'
          disabled={obj.status === 0}
          checked={obj.is_scanner_enable === 1}
          onChange={() => handleAutoCrop(obj)}
        />
      </td>
      <td className="flex justify-center items-center h3" style={{ width: '12.5%' }}>
        <Switch
          size='sm'
          disabled={obj.status === 0}
          checked={obj.is_grayscale_enable === 1}
          onChange={() => handleGrayScaleImage(obj)}
        />
      </td>
      <td className="flex justify-center items-center h3" style={{ width: '12.5%' }}>
        <Radio
          key={`pod-${state.renderKey}`}
          size='sm'
          disabled={obj.status === 0}
          checked={obj.is_pod === 1}
          onClick={() => {
            if (obj.is_pod === 1) {
              removePod();
            } else {
              setIsPod(obj);
            }
          }}
        />
      </td>
      <td className="flex justify-center items-center h3" style={{ width: '12.5%' }}>
        <Radio
          key={`bol-${state.renderKey}`}
          size='sm'
          disabled={obj.status === 0}
          checked={obj.is_bol === 1}
          onClick={() => {
            if (obj.is_bol === 1) {
              removeBOL();
            } else {
              setIsBOL(obj);
            }
          }}
        />
      </td>
      <td className="flex justify-center items-center h3" style={{ width: '12.5%' }}>
        <Radio
          key={`osd-${state.renderKey}`}
          size='sm'
          disabled={obj.status === 0}
          checked={obj.is_osd === 1}
          onClick={() => {
            if (obj.is_osd === 1) {
              removeOsd();
            } else {
              setIsOsd(obj);
            }
          }}
        />
      </td>
      <td className="f5 flex justify-center items-center h3" style={{ width: '12.5%' }}>
        <Switch
          size='sm'
          disabled={obj.status === 0}
          checked={obj.partner_visibility === 1}
          onChange={() => handlePartnerVisibility(obj)}
        />
      </td>
    </tr>
  ), [
    handleActiveInactive, 
    handleAutoCrop, 
    handleGrayScaleImage, 
    handlePartnerVisibility, 
    setIsPod, 
    setIsBOL, 
    setIsOsd, 
    removePod, 
    removeBOL, 
    removeOsd,
    state.renderKey
  ]);

  if (Access.allow_artifax !== 1) {
    return (
      <div role="alert" className="max-w-2xl mx-auto mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg shadow-sm">
        <div className="flex items-center space-x-3">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-yellow-800">Please subscribe to <b>Shipment Documents</b> feature</span>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {state.fetching ? (
        <div className="flex flex-col justify-center items-center mt-8 p-8 bg-white rounded-xl shadow-lg border border-gray-100">
          <Loading size="lg" color="primary" className="mb-4">
            <Text size={16} weight="medium" className="text-gray-700">
              Fetching Shipment Artifax™...
            </Text>
          </Loading>
          <Text size={14} className="text-gray-500">
            Please wait while we load your document types
          </Text>
        </div>
      ) : (
        <>
          {Access.allow_artifax === 1 && state.artifactsList.length !== 0 && (
            <div className="px-2 pb-4">
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 border-2 border-dotted">
                <div className="flex justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-800">
                    Shipping Document Types
                  </h2>
                  <NextButton
                    icon={<PaperPlus/>}
                    auto
                    css={{br: 0}}
                    onClick={() => setState(prev => ({ ...prev, addArtifacts: true }))}
                  >
                    <Text color="white" size="1rem">Create Artifax™ Type</Text>
                  </NextButton>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="flex pb-3">
                        <th className="flex flex-start items-center text-blue-gray-900 gap-2 font-bold opacity-70" style={{ width: '12.5%' }}>
                          Document Types
                        </th>
                        <th className="flex justify-center items-center text-blue-gray-900 font-bold opacity-70" style={{ width: '12.5%' }}>
                          Active
                        </th>
                        <th className="flex justify-center items-center text-blue-gray-900 font-bold opacity-70" style={{ width: '12.5%' }}>
                          Auto Crop Image
                        </th>
                        <th className="flex justify-center items-center text-blue-gray-900 font-bold opacity-70" style={{ width: '12.5%' }}>
                          Grayscale Image
                        </th>
                        <th className="flex justify-center items-center text-blue-gray-900 font-bold opacity-70" style={{ width: '12.5%' }}>
                          Is POD
                        </th>
                        <th className="flex justify-center items-center text-blue-gray-900 font-bold opacity-70" style={{ width: '12.5%' }}>
                          Is BOL
                        </th>
                        <th className="flex justify-center items-center text-blue-gray-900 font-bold opacity-70" style={{ width: '12.5%' }}>
                          Is OS&D
                        </th>
                        <th className="flex justify-center items-center text-blue-gray-900 font-bold opacity-70" style={{ width: '12.5%' }}>
                          Partner Visibility
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.artifactsList.map((obj, idx) => renderUserRow(obj, idx))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <Modal
            closeButton
            blur
            preventClose
            aria-labelledby="Add Message"
            open={state.addArtifacts}
            onClose={() => setState(prev => ({ ...prev, addArtifacts: false }))}
          >
            <Modal.Header>
              <Text id="modal-title" size={18}>
                Add a new Artifax™ Type
              </Text>
            </Modal.Header>

            <Modal.Body css={{py: 40}}>
              <Input 
                underlined
                clearable 
                bordered 
                required 
                labelPlaceholder="Your Artifax Name"
                value={state.addArtifactsName}
                onChange={(e) => setState(prev => ({ ...prev, addArtifactsName: e.target.value }))}
              />
            </Modal.Body>

            <Modal.Footer>
              <NextButton
                auto
                onClick={addArtifacts}
              >
                <Text color="white">Submit</Text>
              </NextButton>
            </Modal.Footer>
          </Modal>

          {!state.fetching && state.artifactsList.length === 0 && (
            <div className="text-center mt-6">
              <div className="text-lg text-gray-700 mb-4">No Artifax™ Found.</div>
              <Button
                text="Add Artifax Type"
                onClick={() => setState(prev => ({ ...prev, addArtifacts: true }))}
              />
            </div>
          )}
        </>
      )}
      
      <CrossCompanySettings/>
    </div>
  );
};

export default ManageArtifacts;